/**
 * Product Ranking Service using OpenAI
 * Ranks and explains products from our database based on user scan data
 * Uses caching to avoid re-running OpenAI for the same scan results
 * All requests go through the Lambda proxy (REACT_APP_API_URL)
 */

import { Product } from '../data/products';
import type { ScanRecord } from './scanService';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface ProductRankingInput {
  scanRecord: ScanRecord;
  candidateProducts: Product[]; // Products to rank (from our database only)
  userGender?: 'male' | 'female' | 'prefer_not_to_say' | 'other';
  userPreferences?: {
    budget?: 'low' | 'medium' | 'high';
    sensitivity?: 'very_sensitive' | 'sensitive' | 'normal';
  };
  limit?: number; // Maximum number of products to return (default: 6)
}

export interface ProductRanking {
  productId: string;
  rank: number; // 1 = best, 2 = second best, etc.
  explanation: string; // Short reason why this product fits the user
  usageTiming: 'AM' | 'PM' | 'AM/PM' | 'As needed';
  safetyNotes?: string[]; // e.g., "Avoid using with retinol", "Use sunscreen during day"
}

export interface ProductRankingResponse {
  rankings: ProductRanking[];
  cached: boolean; // Whether this result came from cache
}

// ============================================================================
// Cache Implementation
// ============================================================================

interface CacheEntry {
  response: ProductRanking[];
  timestamp: number;
  cacheKey: string;
}

// In-memory cache (can be enhanced with localStorage for persistence)
const cache = new Map<string, CacheEntry>();
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

function generateCacheKey(input: ProductRankingInput): string {
  // Create a deterministic cache key based on scan data and product IDs
  const scanId = input.scanRecord.scanId;
  const productIds = input.candidateProducts.map(p => p.id).sort().join(',');
  const gender = input.userGender || 'none';
  const budget = input.userPreferences?.budget || 'none';
  const sensitivity = input.userPreferences?.sensitivity || 'none';
  
  // Use scan analysis data for cache key (if scan has changed, cache invalidates)
  const analysisHash = input.scanRecord.analysis
    ? JSON.stringify({
        severity: input.scanRecord.analysis.acneAnalysis?.overallSeverity,
        pH: input.scanRecord.analysis.acneAnalysis?.estimatedPH,
        skinType: input.scanRecord.analysis.acneAnalysis?.textureSummary?.substring(0, 50),
      })
    : 'no-analysis';
  
  return `product_ranking:${scanId}:${analysisHash}:${productIds}:${gender}:${budget}:${sensitivity}`;
}

function getCachedRanking(cacheKey: string): ProductRanking[] | null {
  const entry = cache.get(cacheKey);
  if (!entry) {
    return null;
  }
  
  const now = Date.now();
  if (now - entry.timestamp > CACHE_TTL) {
    cache.delete(cacheKey);
    return null;
  }
  
  return entry.response;
}

function setCachedRanking(cacheKey: string, rankings: ProductRanking[]): void {
  cache.set(cacheKey, {
    response: rankings,
    timestamp: Date.now(),
    cacheKey,
  });
}

// ============================================================================
// OpenAI Prompt Construction
// ============================================================================

function buildSystemPrompt(): string {
  return `You are a skincare expert AI assistant that ranks skincare products based on user skin analysis data.

CRITICAL RULES:
1. You MUST ONLY rank products from the provided product list. NEVER invent, add, or suggest products that are not in the list.
2. You MUST use the exact product IDs provided. Do NOT modify or create new product IDs.
3. Rank products from BEST (rank 1) to WORST (higher rank numbers) based on how well they match the user's skin type, acne severity, and concerns.
4. Provide a SHORT explanation (1-2 sentences) for each product explaining why it fits the user.
5. Suggest AM/PM usage timing based on product type and ingredients.
6. Include safety notes for ingredient conflicts (e.g., retinol + AHA, sunscreen requirements, etc.).

Your response MUST be valid JSON matching this exact schema:
{
  "rankings": [
    {
      "productId": "exact-product-id-from-list",
      "rank": 1,
      "explanation": "Brief explanation why this product fits",
      "usageTiming": "AM" | "PM" | "AM/PM" | "As needed",
      "safetyNotes": ["note1", "note2"] // optional array
    }
  ]
}`;
}

function buildUserPrompt(input: ProductRankingInput): string {
  const analysis = input.scanRecord.analysis;
  const acneAnalysis = analysis?.acneAnalysis;
  
  // Extract user skin data
  const skinType = acneAnalysis?.textureSummary || analysis?.textureNotes || 'unknown';
  const acneSeverity = acneAnalysis?.overallSeverity || analysis?.acneSeverity || 'unknown';
  const estimatedPH = acneAnalysis?.estimatedPH ?? analysis?.estimatedPH;
  const regions = acneAnalysis?.regions || [];
  const concerns: string[] = [];
  
  // Build concerns list
  if (acneAnalysis?.acneType?.inflammatory === 'moderate' || acneAnalysis?.acneType?.inflammatory === 'pronounced') {
    concerns.push('inflammation', 'redness');
  }
  if (acneAnalysis?.acneType?.comedonal === 'moderate' || acneAnalysis?.acneType?.comedonal === 'pronounced') {
    concerns.push('clogged pores', 'blackheads');
  }
  const hyperpigmentationRegions = regions.filter(r => r.hyperpigmentationLevel !== 'none');
  if (hyperpigmentationRegions.length > 0) {
    concerns.push('dark spots', 'hyperpigmentation');
  }
  if (estimatedPH && estimatedPH < 4.7) {
    concerns.push('damaged barrier');
  }
  
  // Build product list for AI (only IDs and key attributes)
  const productList = input.candidateProducts.map(p => ({
    id: p.id,
    brand: p.brand,
    category: p.productCategory,
    skinTypes: p.recommendedFor.skinTypes,
    acneTypes: p.recommendedFor.acneTypes,
    concerns: p.recommendedFor.concerns,
    keyIngredients: p.keyIngredients,
    priceRange: p.priceRange,
    tags: p.tags,
  }));
  
  const userInfo = `
USER SKIN ANALYSIS:
- Skin Type: ${skinType}
- Acne Severity: ${acneSeverity}
- Estimated pH: ${estimatedPH ? estimatedPH.toFixed(2) : 'unknown'}
- Primary Concerns: ${concerns.length > 0 ? concerns.join(', ') : 'general skin health'}
- Gender Preference: ${input.userGender || 'not specified'}
- Budget: ${input.userPreferences?.budget || 'not specified'}
- Sensitivity: ${input.userPreferences?.sensitivity || 'not specified'}
`;

  const productsJson = JSON.stringify(productList, null, 2);
  
  return `${userInfo}

PRODUCTS TO RANK (ONLY USE PRODUCTS FROM THIS LIST):
${productsJson}

TASK:
Rank these ${input.candidateProducts.length} products from BEST to WORST for this user.
Return ONLY valid JSON with the exact schema specified.
Use ONLY the product IDs provided above.
Do NOT add, modify, or invent any products.`;
}

// ============================================================================
// JSON Response Parsing and Validation
// ============================================================================

function validateRankingResponse(data: any): ProductRanking[] {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid response: not an object');
  }
  
  if (!Array.isArray(data.rankings)) {
    throw new Error('Invalid response: rankings is not an array');
  }
  
  const rankings: ProductRanking[] = [];
  
  for (const item of data.rankings) {
    if (!item.productId || typeof item.productId !== 'string') {
      throw new Error('Invalid ranking: missing or invalid productId');
    }
    if (typeof item.rank !== 'number' || item.rank < 1) {
      throw new Error('Invalid ranking: rank must be a positive number');
    }
    if (!item.explanation || typeof item.explanation !== 'string') {
      throw new Error('Invalid ranking: missing or invalid explanation');
    }
    if (!['AM', 'PM', 'AM/PM', 'As needed'].includes(item.usageTiming)) {
      throw new Error('Invalid ranking: usageTiming must be AM, PM, AM/PM, or As needed');
    }
    
    rankings.push({
      productId: item.productId,
      rank: item.rank,
      explanation: item.explanation,
      usageTiming: item.usageTiming as 'AM' | 'PM' | 'AM/PM' | 'As needed',
      safetyNotes: Array.isArray(item.safetyNotes) ? item.safetyNotes : undefined,
    });
  }
  
  return rankings;
}

// ============================================================================
// Main Service Function
// ============================================================================

export async function rankProductsForUser(
  input: ProductRankingInput
): Promise<ProductRankingResponse> {
  // Check cache first
  const cacheKey = generateCacheKey(input);
  const cached = getCachedRanking(cacheKey);
  
  if (cached) {
    return {
      rankings: cached,
      cached: true,
    };
  }
  
  // Validate input
  if (!input.scanRecord?.analysis) {
    throw new Error('Scan record must have analysis data');
  }
  
  if (!input.candidateProducts || input.candidateProducts.length === 0) {
    throw new Error('Candidate products list cannot be empty');
  }
  
  // Limit products if too many (OpenAI context limits)
  const productsToRank = input.candidateProducts.slice(0, 50);
  if (productsToRank.length < input.candidateProducts.length) {
    console.warn(`Limiting product ranking to 50 products (was ${input.candidateProducts.length})`);
  }
  
  // Build prompts
  const systemPrompt = buildSystemPrompt();
  const userPrompt = buildUserPrompt({
    ...input,
    candidateProducts: productsToRank,
  });
  
  try {
    const rawUrl = process.env.REACT_APP_API_URL;
    if (typeof rawUrl !== 'string' || !rawUrl.trim().startsWith('http')) {
      throw new Error('REACT_APP_API_URL is required for product ranking. Set it to your Lambda/API base URL.');
    }
    const backendUrl = rawUrl.trim().replace(/\/+$/, '');

    const response = await fetch(`${backendUrl}/api/openai-chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        temperature: 0.3,
        max_tokens: 2000,
        response_format: { type: 'json_object' },
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as { error?: string | { message?: string }; message?: string };
      const msg = typeof errorData?.error === 'string' ? errorData.error : errorData?.error?.message ?? errorData?.message;
      throw new Error(msg || `API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    const content = data.choices?.[0]?.message?.content;
    
    if (!content) {
      throw new Error('No response content from OpenAI');
    }
    
    // Parse JSON response
    let parsedData;
    try {
      parsedData = JSON.parse(content);
    } catch (parseError) {
      // Try to extract JSON from markdown code blocks if present
      const jsonMatch = content.match(/```(?:json)?\s*(\{[\s\S]*\})\s*```/);
      if (jsonMatch) {
        parsedData = JSON.parse(jsonMatch[1]);
      } else {
        throw new Error('Failed to parse JSON response from OpenAI');
      }
    }
    
    // Validate and extract rankings
    const rankings = validateRankingResponse(parsedData);
    
    // Verify all product IDs exist in candidate list
    const productIdSet = new Set(productsToRank.map(p => p.id));
    for (const ranking of rankings) {
      if (!productIdSet.has(ranking.productId)) {
        throw new Error(`Ranking contains invalid product ID: ${ranking.productId}`);
      }
    }
    
    // Limit results
    const limit = input.limit || 6;
    const limitedRankings = rankings.slice(0, limit);
    
    // Cache the result
    setCachedRanking(cacheKey, limitedRankings);
    
    return {
      rankings: limitedRankings,
      cached: false,
    };
  } catch (error) {
    console.error('Error ranking products with OpenAI:', error);
    throw error;
  }
}

// ============================================================================
// Helper Function: Get Ranked Products (combines ranking with product data)
// ============================================================================

export interface RankedProduct extends Product {
  rank: number;
  explanation: string;
  usageTiming: 'AM' | 'PM' | 'AM/PM' | 'As needed';
  safetyNotes?: string[];
}

export async function getRankedProducts(
  input: ProductRankingInput
): Promise<RankedProduct[]> {
  const response = await rankProductsForUser(input);
  
  // Map rankings to full product objects
  const productMap = new Map(input.candidateProducts.map(p => [p.id, p]));
  
  const rankedProducts: RankedProduct[] = [];
  for (const ranking of response.rankings) {
    const product = productMap.get(ranking.productId);
    if (product) {
      rankedProducts.push({
        ...product,
        rank: ranking.rank,
        explanation: ranking.explanation,
        usageTiming: ranking.usageTiming,
        safetyNotes: ranking.safetyNotes,
      });
    }
  }
  
  return rankedProducts;
}

// ============================================================================
// Cache Management
// ============================================================================

export function clearProductRankingCache(): void {
  cache.clear();
}

export function clearProductRankingCacheForScan(scanId: string): void {
  const keysToDelete: string[] = [];
  Array.from(cache.keys()).forEach(key => {
    if (key.includes(`:${scanId}:`)) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach(key => cache.delete(key));
}
